import { lazyAppContext } from '@1inch-community/core/lazy';
import { BehaviorSubject, combineLatest, defer, filter, from, map, merge, of, shareReplay, startWith, switchMap, } from 'rxjs';
import { fromEIP1193Event } from './from-eip1193-event';
export class ProviderDataAdapter {
    constructor(info) {
        this.info = info;
        this.context = lazyAppContext('ProviderDataAdapter');
        this.provider$ = new BehaviorSubject(null);
        this.activeAddressInner$ = new BehaviorSubject(null);
        this.providerOrDisconnect$ = merge(this.provider$, defer(() => this.disconnect$).pipe(map(() => null)));
        this.addresses$ = this.providerOrDisconnect$.pipe(switchMap((provider) => {
            if (!provider)
                return of([]);
            return from(this.getAddresses()).pipe(switchMap((addresses) => fromEIP1193Event(provider, 'accountsChanged').pipe(startWith(addresses))));
        }), shareReplay({ bufferSize: 1, refCount: true }));
        this.activeAddress$ = combineLatest([
            this.addresses$,
            this.activeAddressInner$,
        ]).pipe(map(([addresses, activeAddress]) => {
            if (activeAddress)
                return activeAddress;
            return addresses[0] ?? null;
        }), shareReplay({ bufferSize: 1, refCount: true }));
        this.chainId$ = this.providerOrDisconnect$.pipe(switchMap((provider) => {
            if (!provider)
                return of(null);
            return from(this.getChainId()).pipe(switchMap((chainId) => fromEIP1193Event(provider, 'chainChanged').pipe(map((chainStr) => {
                return parseChainId(chainStr);
            }), startWith(chainId))));
        }), shareReplay({ bufferSize: 1, refCount: true }));
        this.disconnect$ = this.provider$.pipe(filter(Boolean), switchMap((provider) => fromEIP1193Event(provider, 'disconnect')));
        this.isConnected$ = this.activeAddress$.pipe(map((address) => !!address));
        this.info$ = of(info);
    }
    async init(context) {
        this.context.set(context);
    }
    getInfo() {
        return this.info;
    }
    setProvider(provider) {
        this.provider$.next(provider);
    }
    async getAddresses() {
        const provider = this.provider$.value;
        if (!provider)
            return [];
        return (await provider.request({ method: 'eth_accounts' }));
    }
    async getActiveAddress() {
        const activeAddressInner = this.activeAddressInner$.value;
        if (activeAddressInner) {
            return activeAddressInner;
        }
        const addresses = await this.getAddresses();
        return addresses[0] ?? null;
    }
    async getChainId() {
        const provider = this.provider$.value;
        if (!provider)
            return null;
        const chainStr = (await provider.request({ method: 'eth_chainId' }).catch(() => null));
        if (!chainStr)
            return null;
        return parseChainId(chainStr);
    }
    async isConnected() {
        const address = await this.getActiveAddress();
        return !!address;
    }
    setActiveAddress(address) {
        this.activeAddressInner$.next(address);
    }
}
function parseChainId(chainId) {
    if (typeof chainId === 'number') {
        return chainId;
    }
    return parseInt(chainId, 16);
}
//# sourceMappingURL=provider-data-adapter.js.map